import { AppFooter } from "@/components/home/AppFooter";
import { AppTopNav, PAGE_CONTAINER_ID } from "@/components/home/AppTopNav";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { userAtom, userIdAtom } from "@/state/auth.state";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useAtomValue } from "jotai";
import { MessageSquareText } from "lucide-react";
import { useEffect, useState } from "react";

type FeedbackType = "general" | "bug" | "feature" | "data";

export function FeedbackPage() {
  const user = useAtomValue(userAtom);
  const userId = useAtomValue(userIdAtom);

  const [email, setEmail] = useState("");
  const [type, setType] = useState<FeedbackType>("general");
  const [message, setMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Prefill email from Supabase user (if available), but allow editing even if signed in
  useEffect(() => {
    const pref = user?.email ?? "";
    setEmail((prev) => (prev.trim().length === 0 ? pref : prev));
  }, [user?.email]);

  const canSubmit = message.trim().length >= 10;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitted(false);

    try {
      const payload = {
        user_id: userId ?? null,
        email: email.trim() || null,
        type,
        message: message.trim(),
      };

      const { error } = await supabase.from("feedback").insert(payload);

      if (error) {
        setSubmitError(error.message);
        return;
      }

      setSubmitted(true);
      setMessage("");
      setType("general");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Seo
        title={t`Feedback`}
        description={t`Share your feedback and help improve Stratistics.`}
        canonicalPath="/feedback/"
      />
      <div
        id={PAGE_CONTAINER_ID}
        className="min-h-screen max-h-full overflow-y-auto"
      >
        {/* Header/Navigation */}
        <AppTopNav />

        {/* Page */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-4 pb-16 pt-32 md:pb-24 md:pt-40">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/70 px-4 py-2 text-sm text-gray-700 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
                <MessageSquareText className="h-4 w-4 text-orange-600" />
                <Trans>Help us improve Stratistics</Trans>
              </div>

              <h1 className="mb-4 text-4xl font-bold tracking-tight text-gray-900 dark:text-white md:text-5xl">
                <Trans>Send feedback</Trans>
              </h1>

              <p className="mx-auto mb-10 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
                <Trans>
                  Tell us what’s working, what’s not, and what you’d like to see
                  next.
                </Trans>
              </p>
            </div>

            <div className="mx-auto max-w-2xl">
              <div className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-lg backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/60">
                {submitted ? (
                  <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4 text-green-900 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-100">
                    <div className="font-medium">
                      <Trans>Thanks — we got your feedback!</Trans>
                    </div>
                    <div className="mt-1 text-sm opacity-90">
                      <Trans>We’ll use it to improve the product.</Trans>
                    </div>
                  </div>
                ) : null}

                {submitError ? (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-100">
                    <div className="font-medium">
                      <Trans>Could not send feedback</Trans>
                    </div>
                    <div className="mt-1 text-sm opacity-90">{submitError}</div>
                  </div>
                ) : null}

                <form className="space-y-5" onSubmit={onSubmit}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        <Trans>Type</Trans>
                      </label>
                      <select
                        value={type}
                        onChange={(e) =>
                          setType(e.target.value as FeedbackType)
                        }
                        className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-orange-900/40"
                      >
                        <option value="general">
                          <Trans>General</Trans>
                        </option>
                        <option value="bug">
                          <Trans>Bug</Trans>
                        </option>
                        <option value="feature">
                          <Trans>Feature request</Trans>
                        </option>
                        <option value="data">
                          <Trans>Data issue</Trans>
                        </option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        <Trans>Email (optional)</Trans>
                      </label>
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="you@example.com"
                        className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-orange-900/40"
                      />
                      {user?.email ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <Trans>
                            Prefilled from your account — you can change it.
                          </Trans>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      <Trans>Your message</Trans>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      placeholder={t`What should we improve? What did you expect to happen?`}
                      className="w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-orange-900/40"
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <Trans>Minimum 10 characters.</Trans>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <Trans>
                        By submitting, you agree we can use this feedback to
                        improve the product.
                      </Trans>
                    </div>

                    <Button
                      type="submit"
                      disabled={!canSubmit || isSubmitting}
                      className="bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg hover:from-orange-500 hover:to-red-500 focus-visible:ring-orange-500"
                    >
                      {isSubmitting ? (
                        <Trans>Sending…</Trans>
                      ) : (
                        <Trans>Send feedback</Trans>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <AppFooter />
      </div>
    </>
  );
}
